import folderApi from '@editor/api/folder';
import userApi from '@editor/api/user';
import { DEFAULT_TAKE } from '@editor/global/constants';
import {
  ACTIVE_PREFIX,
  LAYER_PREFIX,
  HISTORY_PREFIX,
  ADD_PREFIX,
  EDIT_PREFIX,
  DESIGN_RENAME,
  SET_BACKGROUND,
  SET_COLOR_PALETTE,
  RESIZE,
  DELETE_TEXTURE,
  ADD_TEMPLATE,
  ADD_GENERATED_DESIGN,
  HISTORY_RESET,
} from '@editor/global/events';
import { Design, Folder } from '@editor/types';
import { uniques } from '@editor/utils';
import { getWorkspaceById, getWorkspaceMe } from '@repo/api-client';
import keyBy from 'lodash/keyBy';
import { StateCreator } from 'zustand';

import { usePromotionStore } from '../promotionStore';
import { useUserStore } from '../userStore';

import { DesignsStore, State } from './types';

export const designsStateCreator: StateCreator<DesignsStore> = (set, get) => ({
  ..._initialState,
  async fetchDesigns(opts): Promise<unknown> {
    const {
      take = DEFAULT_TAKE,
      sort = undefined,
      folderId = 'root',
      refresh = false,
      workspaceId,
    } = opts || {};
    const { finishedDesigns, isFetchingDesigns } = get();

    if (refresh) {
      set({
        designsSkip: 0,
        designElements: [],
        totalDesigns: 0,
      });
    }

    const { designsSkip } = get();

    if ((finishedDesigns || isFetchingDesigns) && !refresh) {
      return;
    }
    set({ isFetchingDesigns: true });

    const { results, total } = await userApi.getDesignsWithoutFolders({
      take,
      skip: designsSkip,
      folderId,
      sort,
      workspaceId,
    });
    if (!results?.length) {
      set((state) => ({
        finishedDesigns: true,
        isFetchingDesigns: false,
        totalDesigns: total || state.totalDesigns || 0,
      }));
      return;
    }

    return set((state) => {
      const { designElements } = state;
      return {
        designElements: uniques([...designElements, ...results]),
        designsSkip: designsSkip + take,
        isFetchingDesigns: false,
        finishedDesigns: results.length + designElements.length === total,
        totalDesigns: total,
      };
    });
  },

  async updateDesignLocks(): Promise<void> {
    const user = useUserStore.getState().user;

    if (typeof user !== 'object' || !user?.isTeam) {
      // locks are only for teams
      return;
    }

    const { results: locks } = await userApi.getLockedDesigns();

    set((state) => {
      const { designElements, folderElements } = state;

      return {
        designElements: _setLockStatus(designElements, locks),
        folderElements: _setLockStatus(folderElements, locks),
      };
    });
  },

  async fetchFolders(options): Promise<void> {
    const {
      take = DEFAULT_TAKE,
      sort = undefined,
      workspaceId,
    } = options || {};
    const { foldersSkip, finishedFolders, isFetchingFolders } = get();

    if (finishedFolders || isFetchingFolders) {
      return;
    }
    set({ isFetchingFolders: true });

    const response = await folderApi.getFolders({
      take,
      skip: foldersSkip,
      sort,
      workspaceId,
    });

    if (!response.success) {
      return;
    }

    const { results, total } = response;

    if (!results?.length) {
      set((state) => ({
        finishedFolders: true,
        isFetchingFolders: false,
        totalFolders: total || state.totalFolders || 0,
      }));
      return;
    }

    set((state) => {
      const { folderElements } = state;
      return {
        folderElements: uniques([...folderElements, ...results]),
        foldersSkip: foldersSkip + take,
        isFetchingFolders: false,
        finishedFolders: results.length + folderElements.length === total,
        totalFolders: total,
      };
    });
  },

  /**
   * fetch designs for the currently active folder
   */
  async fetchFolder(
    take = DEFAULT_TAKE,
    sort = undefined,
    workspaceId = undefined
  ): Promise<void> {
    const {
      activeFolder,
      folderSkip: skip,
      folderFinished: finished,
      folderIsFetching: isFetching,
    } = get();

    if (finished || isFetching || !activeFolder) {
      return;
    }
    set({ folderIsFetching: true });

    const { results, total } = await userApi.getDesigns(
      take,
      skip,
      activeFolder.id,
      sort,
      workspaceId
    );
    if (!results?.length) {
      set((state) => ({
        folderFinished: true,
        folderIsFetching: false,
        folderTotal: total || state.folderTotal || 0,
      }));
      return;
    }

    set((state) => {
      const { folderDesigns: designs } = state;
      return {
        folderDesigns: uniques([...designs, ...results]),
        folderSkip: skip + take,
        folderIsFetching: false,
        folderFinished: results.length < take,
        folderTotal: total,
      };
    });
  },

  /**
   * set the active folder
   */
  async setFolder(
    folder: (Partial<Folder> & { id: string }) | null
  ): Promise<void> {
    const activeFolder = get().activeFolder;

    // update folder information if closed
    if (activeFolder?.id && activeFolder.id !== folder?.id) {
      get().updateFolder(activeFolder.id);
    }

    // complete folder information
    if (folder && !folder.name) {
      folder = await get().updateFolder(folder.id);
    }

    set({
      activeFolder: (folder as Folder) || null,
      folderDesigns: [],
      folderFinished: false,
      folderSkip: 0,
      folderIsFetching: false,
      folderTotal: null,
    });
  },

  /**
   * reset all data stored in this store
   */
  reset(): void {
    set({
      designElements: [],
      folderElements: [],
      designsSkip: 0,
      foldersSkip: 0,
      isFetchingDesigns: false,
      isFetchingFolders: false,
      finishedDesigns: false,
      finishedFolders: false,
      totalDesigns: null,
      totalFolders: null,
      active: null,
      activeDesign: null,
    });
    get().setFolder(null);
  },

  /**
   * delete an element, whether a design, folder, or a design in active folder
   */
  deleteElement(id: string): void {
    set((state) => {
      if (_isInDesignElements(state, id)) {
        return {
          designElements: state.designElements.filter((elem) => elem.id !== id),
          totalDesigns: (state.totalDesigns ?? 1) - 1,
          designsSkip: state.designsSkip - 1,
        };
      }

      if (_isInFolderElements(state, id)) {
        return {
          folderElements: state.folderElements.filter((elem) => elem.id !== id),
          totalFolders: (state.totalFolders ?? 1) - 1,
          foldersSkip: state.foldersSkip - 1,
        };
      }

      if (_isInFolder(state, id)) {
        return {
          folderDesigns: state.folderDesigns.filter((elem) => elem.id !== id),
          folderTotal: (state.folderTotal ?? 1) - 1,
          folderSkip: state.folderSkip - 1,
        };
      }

      // if not found, do nothing
      // empty object is required, otherwise state is unset
      return state;
    });
  },

  /**
   * update an element stored in state
   * if the element is not present in the current state, add it
   */
  updateElement(element: Design | Folder): void {
    set((state) => {
      let activeDesign = state.activeDesign;
      let activeFolder = state.activeFolder;
      if (isDesignElement(element) && element.id === state.active) {
        activeDesign = element;
      }
      if (isFolderElement(element) && element.id === state.activeFolder?.id) {
        activeFolder = element;
      }

      const wasInFolder = _isInFolder(state, element.id);
      if (isDesignElement(element) && element.folderId) {
        if (element.folderId === state.activeFolder?.id) {
          if (wasInFolder) {
            // update in folder
            return {
              activeDesign,
              folderDesigns: state.folderDesigns.map((elem) =>
                element.id === elem.id ? { ...elem, ...element } : elem
              ),
            };
          }
          // add to folder
          return {
            activeDesign,
            designElements: state.designElements.filter(
              (e) => e.id !== element.id
            ),
            totalDesigns: (state.totalDesigns ?? 1) - 1,
            folderDesigns: [element, ...state.folderDesigns],
            folderTotal: (state.folderTotal ?? 0) + 1,
          };
        }
        // remove from state
        return {
          activeDesign,
          designElements: !wasInFolder
            ? state.designElements.filter(({ id }) => id !== element.id)
            : state.designElements,
          totalDesigns: !wasInFolder
            ? (state.totalDesigns ?? 1) - 1
            : state.totalDesigns,
          folderDesigns: wasInFolder
            ? state.folderDesigns.filter(({ id }) => id !== element.id)
            : state.folderDesigns,
          folderTotal: wasInFolder
            ? (state.folderTotal ?? 1) - 1
            : state.folderTotal,
        };
      }

      let updatedStateProps: Partial<DesignsStore> = {
        activeDesign,
        activeFolder,
      };

      if (!element.type) {
        // some old design data did not have a type property yet
        (element as Design).type = 'design';
      }

      if (element.type === 'design') {
        if (_isInDesignElements(state, element.id)) {
          updatedStateProps = {
            ...updatedStateProps,
            designElements: state.designElements.map((elem) =>
              element.id === elem.id ? { ...elem, ...element } : elem
            ),
          };
        } else {
          updatedStateProps = {
            ...updatedStateProps,
            designElements: [element, ...state.designElements],
            totalDesigns: (state.totalDesigns ?? 0) + 1,
            folderDesigns: wasInFolder
              ? state.folderDesigns.filter(({ id }) => id !== element.id)
              : state.folderDesigns,
            folderTotal: wasInFolder
              ? (state.folderTotal ?? 1) - 1
              : state.folderTotal,
          };
        }
      } else {
        if (_isInFolderElements(state, element.id)) {
          updatedStateProps = {
            ...updatedStateProps,
            folderElements: state.folderElements.map((elem) =>
              element.id === elem.id ? { ...elem, ...element } : elem
            ),
          };
        } else {
          updatedStateProps = {
            ...updatedStateProps,
            folderElements: [element, ...state.folderElements],
            totalFolders: (state.totalFolders ?? 0) + 1,
          };
        }
      }

      return updatedStateProps;
    });
  },

  /**
   * update the data of a folder in state
   * main reason is to have correct previews
   */
  async updateFolder(id: string): Promise<Folder | null> {
    const response = await folderApi.getFolder({ id });

    if (!response.success || !response?.folder) {
      return null;
    }

    set((state) => ({
      folderElements: [
        response.folder,
        ...state.folderElements.filter((e) => e.id !== id),
      ],
    }));

    return response.folder;
  },

  /**
   * insert a new element into state
   */
  insertElement(element: Design | Folder): void {
    if (
      isDesignElement(element) &&
      element.folderId &&
      element.folderId === get().activeFolder?.id
    ) {
      set((state) => ({
        folderDesigns: [element, ...state.folderDesigns],
        folderTotal: (state.folderTotal ?? 0) + 1,
      }));
      return;
    }

    set((state) => {
      if (element.type === 'folder') {
        return {
          folderElements: [element, ...state.folderElements],
          totalFolders: (state.totalFolders ?? 0) + 1,
        };
      } else {
        return {
          designElements: [element, ...state.designElements],
          totalDesigns: (state.totalDesigns ?? 0) + 1,
        };
      }
    });
  },

  /**
   * set the active design
   */
  setActive(id: string): void {
    set((state) => {
      if (_isInFolder(state, id)) {
        return {
          active: id,
          activeDesign: state.folderDesigns.find((e) => e.id === id) ?? null,
          // element with specified id if found will go on top and keeping the order of other elements
          folderDesigns: [...state.folderDesigns].sort((a) =>
            a.id === id ? -1 : 1
          ),
        };
      }

      if (_isInDesignElements(state, id)) {
        return {
          active: id,
          activeDesign: state.designElements?.find((x) => x.id === id) ?? null,
          // element with specified id if found will go on top and keeping the order of other elements
          designElements: [...state.designElements].sort((a) =>
            a.id === id ? -1 : 1
          ),
        };
      }

      return {
        active: id,
      };
    });
  },

  /**
   * get the active design
   */
  async getActive(): Promise<Design | null> {
    if (get().activeDesign) {
      return get().activeDesign;
    }

    let elements = get().designElements;
    if (!elements.length) {
      await get().fetchDesigns();
      elements = get().designElements;
    }
    elements = [...elements, ...get().folderDesigns].filter(
      (element) => element.type === 'design'
    );

    if (!elements.length) {
      return null;
    }
    const activeId = get().active;
    const active = elements.find((e) => e.id === activeId) ?? elements[0];
    return active;
  },

  setIsActiveModified(eventKey: string): void {
    const eventKeySections = eventKey?.split(':') ?? [];

    const isModified = _isArtboardEventModifier(
      get().isActiveModified,
      eventKeySections
    );

    set(() => ({ isActiveModified: isModified }));
  },

  forceActiveModified(): void {
    set({ isActiveModified: true });
  },

  setAddToExistingDesignParams(urlParams: string): void {
    set({ addToExistingDesignParams: urlParams });
  },

  closeProjectListModal(): void {
    set({ showProjectListModal: false, addToExistingDesignParams: null });
  },

  setShowProjectListModal(value: boolean): void {
    set({ showProjectListModal: value });
  },

  setShowLimitModal(value: boolean): void {
    if (value) {
      const user = useUserStore.getState().user;
      const isPro = user ? user.plan === 'PRO' : false;
      const type = isPro ? 'proProjectLimitReached' : 'projectLimitReached';
      usePromotionStore.getState().showUpgradeModal(type);
    } else {
      usePromotionStore.getState().closeUpgradeModal();
    }
  },

  /**
   * check whether the current user can create a new design
   */
  async canUserCreateDesign(options): Promise<boolean> {
    const { workspaceId, canShowProjectListModal, cb, redirectUrl } =
      options || {};
    let workspace;
    if (workspaceId) {
      workspace = await getWorkspaceById(workspaceId);
    } else {
      workspace = await getWorkspaceMe();
    }
    if (!workspace.success) {
      return false;
    }
    const { canCreateDesigns } = workspace.workspace;

    if (!canCreateDesigns) {
      if (canShowProjectListModal && redirectUrl) {
        get().setShowProjectListModal(true);
        get().setAddToExistingDesignParams(redirectUrl);
      } else {
        get().setShowLimitModal(true);
      }
      return false;
    }
    cb?.();
    return true;
  },
});

const _initialState: State = {
  designElements: [],
  folderElements: [],
  designsSkip: 0,
  foldersSkip: 0,
  finishedDesigns: false,
  finishedFolders: false,
  isFetchingDesigns: false,
  isFetchingFolders: false,
  totalDesigns: null,
  totalFolders: null,
  activeFolder: null,
  folderDesigns: [],
  folderFinished: false,
  folderSkip: 0,
  folderIsFetching: false,
  folderTotal: null,
  active: null,
  activeDesign: null,
  isActiveModified: false,
  showProjectListModal: false,
  addToExistingDesignParams: null,
};

type MODIFIER_EVENT_FIRST_KEYS_TYPE =
  | typeof ACTIVE_PREFIX
  | typeof LAYER_PREFIX
  | typeof HISTORY_PREFIX
  | typeof ADD_PREFIX
  | typeof EDIT_PREFIX;

const MODIFIER_EVENT_FIRST_KEYS: MODIFIER_EVENT_FIRST_KEYS_TYPE[] = [
  ACTIVE_PREFIX,
  LAYER_PREFIX,
  HISTORY_PREFIX,
  ADD_PREFIX,
  EDIT_PREFIX,
];

const MODIFIER_EVENT_SECOND_KEYS = [
  DESIGN_RENAME.split(':')[1],
  SET_BACKGROUND.split(':')[1],
  SET_COLOR_PALETTE.split(':')[1],
  RESIZE.split(':')[1],
  DELETE_TEXTURE.split(':')[1],
];

const NOT_MODIFIER_EVENT_KEYS = [
  ADD_TEMPLATE.split(':')[1],
  ADD_GENERATED_DESIGN.split(':')[1],
  HISTORY_RESET.split(':')[1],
];

const _setLockStatus = <T extends Design | Folder>(
  designs: T[],
  locks: Design[]
): T[] => {
  const lockedDesignsMap = keyBy(locks, (x) => x.id);

  return designs.map((design) => {
    const { id } = design;

    // locks contains all locked designs.
    // If the current design is not in the list, it is not locked anymore
    return { ...design, lockedBy: lockedDesignsMap[id]?.lockedBy || null };
  });
};

const _isArtboardEventModifier = (
  isActiveDesignModified: boolean,
  eventKeySections: string[]
): boolean => {
  if (NOT_MODIFIER_EVENT_KEYS.includes(eventKeySections[1])) {
    return false;
  }
  if (
    MODIFIER_EVENT_FIRST_KEYS.includes(
      eventKeySections[0] as MODIFIER_EVENT_FIRST_KEYS_TYPE
    )
  ) {
    return true;
  }
  if (MODIFIER_EVENT_SECOND_KEYS.includes(eventKeySections[1])) {
    return true;
  }
  return isActiveDesignModified;
};

const _isInDesignElements = (
  state: Pick<DesignsStore, 'designElements'>,
  id: string
): boolean => !!state.designElements.find((elem) => elem.id === id);

const _isInFolderElements = (
  state: Pick<DesignsStore, 'folderElements'>,
  id: string
): boolean => !!state.folderElements.find((elem) => elem.id === id);

const _isInFolder = (
  state: Pick<DesignsStore, 'folderDesigns'>,
  id: string
): boolean => !!state.folderDesigns.find((elem) => elem.id === id);

const isFolderElement = (element: Design | Folder): element is Folder => {
  return element.type === 'folder';
};

const isDesignElement = (element: Design | Folder): element is Design => {
  return element.type === 'design';
};
